"use client";

import dynamic from "next/dynamic";
import { FC, useEffect, useRef, useState } from "react";

import Icon from "@/components//ui/Icon/Icon";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Image from "@/components/ui/Image/Image";
import { ctaClickTracking } from "@/dataLayer/dataLayer";
import useMediaQuery from "@/hooks/useMediaQuery";
import { VideoCardListProps } from "@/types";

const VideoCardList: FC<VideoCardListProps> = ({ entry, isCurrentSlide, openVideoModal }) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  useEffect(() => {
    if (videoRef.current?.paused && !isPlaying) {
      videoRef.current?.play();
    } else {
      videoRef.current?.pause();
    }
  }, [isPlaying]);

  const togglePlay = () => {
    setIsPlaying((currentStatus) => !currentStatus);
    ctaClickTracking(!isPlaying ? "pause" : "play", "VideoCardList", "VideoCardList");
  };

  useEffect(() => {
    if (isDesktop && isCurrentSlide !== undefined) {
      setIsPlaying(!isCurrentSlide);
    }
  }, [isDesktop, isCurrentSlide]);

  const handleModal = () => {
    openVideoModal({
      title: entry.title,
      video: entry.video,
    });
    togglePlay();
  };

  return (
    <article className={`relative rounded-lg overflow-hidden shadow-strong mb-m w-[256px] ${!isDesktop && "h-[311px]"} md:w-full`}>
      {entry.video && (isDesktop || (!isDesktop && !entry.backupVideoImage)) && (
        <video
          ref={videoRef}
          className="object-cover w-full h-full"
          src={entry.video.url}
          autoPlay={entry.isCurrentSlide ? true : undefined}
          muted
          loop
          playsInline
        >
          <source src={entry.video.url} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}
      {entry.video && !isDesktop && entry.backupVideoImage && (
        <div className="relative">
          <Image alt={entry.backupVideoImage.url} className="aspect-3-4 rounded-lg" src={entry.backupVideoImage.url} width={256} height={311} />
          <div className="absolute w-full h-full top-0 left-0 inset-0 img-overlay-2"></div>
        </div>
      )}

      <div className="absolute bottom-xs w-full left-0 z-10 text-white p-m">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-medium uppercase font-secondary md:font-primary text-m md:text-m mb-s">{entry.title}</h3>
            {entry.video?.url && entry.cta && (
              <Button
                size={ButtonSize.SMALL}
                variant={ButtonVariant.PRIMARY}
                theme={ButtonTheme.WHITE}
                onClick={handleModal}
                customClass="flex items-center gap-xxs border-none pr-[20px] font-medium "
              >
                {entry.cta.label}
              </Button>
            )}
          </div>

          {(isDesktop || !entry.backupVideoImage) && (
            <Button
              size={ButtonSize.SMALL}
              customClass="md:flex items-center border-none w-fit h-fit p-xs font-medium box-material-primary backdrop-blur-xl overflow-hidden absolute bottom-l right-l rounded-3xl"
              onClick={togglePlay}
            >
              <Icon name={!isPlaying ? "pause" : "play"} size="24" title="Pause" className="stroke-white" />
            </Button>
          )}
        </div>
      </div>
    </article>
  );
};

export default VideoCardList;
